<template>
  <div
    class="w-full min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 py-10 px-4"
  >
    <!-- CARD DE CARREGAMENTO -->
    <div
      v-if="loading"
      class="bg-white onest-font w-full max-w-sm sm:max-w-md p-10 rounded-3xl flex flex-col items-center justify-center shadow-2xl text-center"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
        class="mb-4"
      ></v-progress-circular>
      <p class="text-gray-600 font-medium">
        Carregando detalhes do seu convite...
      </p>
    </div>

    <!-- CARD DE ERRO / EXPIRADO -->
    <div
      v-else-if="erro"
      class="bg-white onest-font w-full max-w-sm sm:max-w-md p-10 rounded-3xl flex flex-col items-center justify-center shadow-2xl text-center"
    >
      <v-icon color="error" size="64" class="mb-4"
        >mdi-alert-circle-outline</v-icon
      >
      <h2 class="font-bold text-2xl text-gray-800 mb-2">
        Convite Indisponível
      </h2>
      <p class="text-gray-600 mb-6">{{ erro }}</p>
      <v-btn
        v-if="!faseExpirada"
        color="primary"
        variant="flat"
        class="rounded-xl font-bold"
        @click="irParaLogin"
      >
        Ir para Login
      </v-btn>
    </div>

    <!-- TELA DE AGRADECIMENTO (RESPOSTA CONCLUÍDA) -->
    <div
      v-else-if="respostaConcluida"
      class="bg-white onest-font w-full max-w-sm sm:max-w-md p-10 rounded-3xl flex flex-col items-center justify-center shadow-2xl text-center"
    >
      <v-icon color="success" size="64" class="mb-4"
        >mdi-checkbox-marked-circle-outline</v-icon
      >
      <h2 class="font-bold text-2xl text-gray-800 mb-2">
        Resposta Registrada!
      </h2>
      <p class="text-gray-600 mb-4">
        Obrigado pela sua resposta. Suas preferências de recusa foram
        registradas.
      </p>
      <div
        class="p-4 bg-gray-50 rounded-xl border border-gray-100 mb-6 text-sm text-gray-500 font-medium"
      >
        Você pode fechar esta página agora.
      </div>
    </div>

    <!-- CARD PRINCIPAL DO CONVITE -->
    <div
      v-else
      class="bg-white onest-font w-full max-w-sm sm:max-w-md md:max-w-xl min-h-[35vh] h-auto p-6 sm:p-10 rounded-3xl flex flex-col justify-between shadow-2xl mx-auto"
    >
      <div>
        <h2 class="font-bold text-3xl sm:text-4xl text-gray-800 mb-2">
          Olá, {{ nomeUsuario }}!
        </h2>
        <p class="mb-4 text-base sm:text-lg text-gray-600">
          Você foi convidado(a) para testar o projeto
          <strong class="text-gray-900">{{ nomeProjeto }}</strong
          >.
        </p>
      </div>

      <div class="flex-1 flex flex-col mt-4">
        <!-- FLUXO 1: SELEÇÃO DE PAPÉIS (ATORES) -->
        <div v-if="!mostrarAceite">
          <p class="font-semibold text-gray-700 text-sm sm:text-base mb-4">
            Selecione os papéis que você aceita testar no sistema (você não é
            obrigado a testar nada):
          </p>

          <div class="flex flex-col gap-4 mb-6">
            <div
              v-for="papel in papeis"
              :key="papel.idAtor"
              class="border border-gray-150 rounded-2xl p-4 bg-gray-50/50 flex flex-col gap-3"
            >
              <div
                class="flex items-center justify-between flex-col sm:flex-row"
              >
                <span class="font-bold text-gray-800 text-sm sm:text-base">{{
                  papel.nomeAtor
                }}</span>
                <v-btn-toggle
                  v-model="papel.aceito"
                  mandatory
                  color="primary"
                  density="compact"
                  variant="outlined"
                  class="rounded-xl border-0 overflow-hidden"
                >
                  <v-btn :value="true" class="!font-bold !text-xs"
                    >Aceitar</v-btn
                  >
                  <v-btn
                    :value="false"
                    class="!font-bold !text-xs"
                    color="error"
                    >Recusar</v-btn
                  >
                </v-btn-toggle>
              </div>

              <!-- Motivo da Recusa (Opcional) -->
              <v-expand-transition>
                <div v-if="!papel.aceito" class="mt-1">
                  <p class="text-xs text-gray-500 mb-1">
                    Motivo da recusa (Opcional):
                  </p>
                  <v-textarea
                    v-model="papel.motivoRecusa"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    rows="2"
                    color="error"
                    class="bg-white rounded-xl text-sm"
                    placeholder="Digite o motivo aqui..."
                  ></v-textarea>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </div>

        <!-- FLUXO 2: FLUXO DE ACEITE (SENHA) -->
        <v-expand-transition>
          <div v-if="mostrarAceite" class="mb-4">
            <template v-if="senhaJaCriada">
              <div
                class="p-4 bg-green-50 border border-green-100 rounded-2xl text-green-900 mb-4"
              >
                <p class="text-sm font-semibold text-center">
                  Você já possui uma conta no sistema.<br />
                  Não é necessário criar uma nova senha. Clique em confirmar
                  para vincular este projeto à sua conta.
                </p>
              </div>
            </template>
            <template v-else>
              <div
                class="p-4 bg-blue-50 border border-blue-100 rounded-2xl text-blue-900 mb-4"
              >
                <p class="text-sm font-semibold">
                  Como você aceitou ao menos um papel, crie uma senha para
                  acessar o Sisgip com o seu e-mail e realizar os testes.
                </p>
              </div>
              <v-form @submit.prevent="confirmarEnvio" ref="form">
                <p class="font-semibold text-gray-700 mb-1 ml-1 text-sm">
                  Nova Senha
                </p>
                <v-text-field
                  v-model="senha"
                  :append-inner-icon="mostrarSenha ? 'mdi-eye-off' : 'mdi-eye'"
                  :type="mostrarSenha ? 'text' : 'password'"
                  @click:append-inner="mostrarSenha = !mostrarSenha"
                  :rules="regrasSenha"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  class="mb-2 bg-white"
                  placeholder="Mínimo 6 caracteres"
                ></v-text-field>

                <p class="font-semibold text-gray-700 mb-1 ml-1 text-sm">
                  Confirmar Senha
                </p>
                <v-text-field
                  v-model="confirmarSenha"
                  :append-inner-icon="
                    mostrarConfirmarSenha ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :type="mostrarConfirmarSenha ? 'text' : 'password'"
                  @click:append-inner="
                    mostrarConfirmarSenha = !mostrarConfirmarSenha
                  "
                  :rules="regrasConfirmacao"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  class="mb-2 bg-white"
                  placeholder="Repita sua senha"
                ></v-text-field>
              </v-form>
            </template>
          </div>
        </v-expand-transition>

        <!-- BOTÕES DE AÇÃO -->
        <div
          class="flex justify-end gap-3 mt-auto pt-6 border-t border-gray-100"
        >
          <!-- Botões da etapa de papéis -->
          <template v-if="!mostrarAceite">
            <v-btn
              v-if="todosRecusados"
              @click="confirmarEnvio"
              variant="flat"
              height="auto"
              color="error"
              :loading="submitting"
              class="!px-6 !py-2.5 !text-white !font-bold !rounded-xl !transition-colors !normal-case tracking-normal shadow"
              prepend-icon="mdi-close-circle-outline"
            >
              Confirmar Recusa
            </v-btn>
            <v-btn
              v-else
              @click="avancarParaSenha"
              variant="flat"
              height="auto"
              color="primary"
              class="!px-6 !py-2.5 !text-white !font-bold !rounded-xl !transition-colors !normal-case tracking-normal shadow"
              prepend-icon="mdi-arrow-right"
            >
              Confirmar Participação
            </v-btn>
          </template>

          <!-- Botões da etapa de senha -->
          <template v-else>
            <v-btn
              @click="voltarAtores"
              variant="outlined"
              height="auto"
              color="primary"
              class="sm:!px-6 sm:!py-2.5 !font-bold !rounded-xl !transition-colors !normal-case tracking-normal"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            <v-btn
              @click="confirmarEnvio"
              variant="flat"
              height="auto"
              color="primary"
              :loading="submitting"
              class="!px-6 !py-2.5 !text-white !font-bold !rounded-xl !transition-colors !normal-case tracking-normal shadow"
              prepend-icon="mdi-check-circle-outline"
            >
              Acessar o Sistema
            </v-btn>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @module TelaVisualizacaoConvite
 * @description Tela de visualização do convite para o usuário que vai acessar o sistema.
 * O usuário recebe um link com um token para acessar essa tela.
 * Caso o usuário aceite ao menos um papel, ele deve criar uma senha para acessar o sistema.
 * Caso o usuário recuse todos os papéis, ele não deve criar uma senha.
 * Caso o usuário não responda dentro do prazo, o convite expira.
 *
 * @property {string} nomeUsuario - Nome do usuário convidado.
 * @property {string} nomeProjeto - Nome do projeto.
 * @property {Array} papeis - Array de papéis do convite.
 * @property {boolean} loading - Controle de estado para carregamento da página.
 * @property {boolean} submitting - Controle de estado para envio de formulário.
 * @property {string|null} erro - Mensagem de erro caso ocorra.
 * @property {boolean} mostrarAceite - Controle de estado para exibição do formulário de criação de senha.
 * @property {boolean} respostaConcluida - Controle de estado indicando se a resposta foi gravada com sucesso.
 * @property {boolean} faseExpirada - Controle de estado para caso de convite com prazo expirado.
 * @property {string} senha - Senha inserida pelo usuário.
 * @property {string} confirmarSenha - Confirmação da senha inserida.
 * @property {boolean} mostrarSenha - Controle para exibição/ocultação do campo de senha.
 * @property {boolean} mostrarConfirmarSenha - Controle para exibição/ocultação do campo de confirmar senha.
 */
import axios from 'axios';

const apiUrl = process.env.VUE_APP_API_URL

export default {
  name: 'TelaVisualizacaoConvite',
  data() {
    return {
      nomeUsuario: 'Carregando...',
      nomeProjeto: 'Carregando...',
      papeis: [],

      // Controles de estado
      loading: true,
      submitting: false,
      erro: null,
      mostrarAceite: false,
      respostaConcluida: false,
      faseExpirada: false,

      // Senhas
      senhaJaCriada: false,
      senha: '',
      confirmarSenha: '',
      mostrarSenha: false,
      mostrarConfirmarSenha: false,
    };
  },
  computed: {
    /**
     * @computed
     * @description Verifica se todos os papéis foram marcados como recusados.
     * @returns {boolean} True se todos os papéis foram recusados.
     */
    todosRecusados() {
      return this.papeis.length > 0 && this.papeis.every((p) => !p.aceito);
    },
    /**
     * @computed
     * @description Validação de regras da senha.
     * @returns {Array<Function>} Array de funções de validação da senha.
     */
    regrasSenha() {
      return [
        (v) => !!v || 'A senha é obrigatória para acessar o sistema',
        (v) => v.length >= 6 || 'A senha deve ter no mínimo 6 caracteres',
      ];
    },
    /**
     * @computed
     * @description Validação de regras da confirmação de senha.
     * @returns {Array<Function>} Array de funções de validação da confirmação de senha.
     */
    regrasConfirmacao() {
      return [
        (v) => !!v || 'A confirmação de senha é obrigatória',
        (v) => v === this.senha || 'As senhas não coincidem',
      ];
    },
  },
  mounted() {
    this.carregarDetalhesConvite();
  },
  methods: {
    /**
     * @function
     * @description Carrega os detalhes do convite baseando-se no token passado na URL.
     * @returns {Promise<void>}
     */
    async carregarDetalhesConvite() {
      const token = this.$route.query.token;
      if (!token) {
        this.erro =
          'Token de convite não encontrado na URL. Por favor, verifique o link enviado no seu e-mail.';
        this.loading = false;

        return;
      }

      this.loading = true;
      try {
        const res = await axios.get(
          `${apiUrl}/projetos/convites/detalhes?token=${token}`,
        );
        this.nomeUsuario = res.data.nomeUsuario;
        this.nomeProjeto = res.data.nomeProjeto;
        this.papeis = res.data.papeis.map((p) => ({
          idAtor: p.idAtor,
          nomeAtor: p.nomeAtor,
          flagAceito: p.flagAceito,
          justificativa: p.justificativa,
          aceito: true, // Default local
          motivoRecusa: '',
        }));

        this.senhaJaCriada = res.data.senhaJaCriada || false;

        if (res.data.faseExpirada) {
          this.erro =
            'Este convite expirou porque a fase de execução da validação do projeto já foi finalizada.';
          this.faseExpirada = true;
        }
      } catch (error) {
        console.error('Erro ao buscar detalhes do convite:', error);
        this.erro =
          error.response?.data?.error ||
          'Token de convite inválido ou expirado.';
      } finally {
        this.loading = false;
      }
    },
    /**
     * @function
     * @description Avança para a etapa de criação de senha.
     * @returns {void}
     */
    avancarParaSenha() {
      this.mostrarAceite = true;
    },
    /**
     * @function
     * @description Volta para a etapa de seleção de papéis.
     * @returns {void}
     */
    voltarAtores() {
      this.mostrarAceite = false;
      this.senha = '';
      this.confirmarSenha = '';
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    /**
     * @function
     * @description Redireciona para o login do Sisgip.
     * @returns {void}
     */
    irParaLogin() {
      this.$router.push('/login');
    },
    /**
     * @function
     * @description Submete as respostas do usuário para o backend.
     * @returns {Promise<void>}
     */
    async confirmarEnvio() {
      // Se aceitou pelo menos um papel e precisa de senha, valida o formulário da senha
      if (!this.todosRecusados && !this.senhaJaCriada) {
        const { valid } = await this.$refs.form.validate();
        if (!valid) {
          return;
        }
      }

      const token = this.$route.query.token;
      const decisoes = this.papeis.map((p) => ({
        idAtor: p.idAtor,
        aceito: p.aceito,
        justificativa: p.aceito
          ? undefined
          : p.motivoRecusa || 'Recusado pelo usuário',
      }));

      this.submitting = true;
      try {
        await axios.post(`${apiUrl}/projetos/convites/responder`, {
          token,
          senha:
            this.todosRecusados || this.senhaJaCriada ? undefined : this.senha,
          decisoes,
        });

        if (this.$toast) {
          this.$toast.success('Resposta salva com sucesso!');
        }

        if (this.todosRecusados) {
          this.respostaConcluida = true;
        } else {
          // Se aceitou, envia para a tela de login
          this.$router.push('/login');
        }
      } catch (error) {
        console.error('Erro ao enviar resposta:', error);
        if (this.$toast) {
          this.$toast.error(
            error.response?.data?.error || 'Erro ao registrar sua resposta.',
          );
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.onest-font {
  font-family: 'Onest', sans-serif !important;
}

:deep(.v-field) {
  border-radius: 0.75rem !important;
}
</style>
